import { useTranslation } from "react-i18next";
import { Spinner, Button } from "flowbite-react";
import {
  HiOutlineInformationCircle,
  HiCog,
  HiOutlineClock,
} from "react-icons/hi";
import { Link } from "react-router-dom";
import {
  useUsageMetrics,
  useFeatureFlag,
  FEATURE_FLAGS,
  useUsageStatus,
  useUsageWidgetConfig,
} from "../../../hooks";
import { integrationPath } from "../../../routes";
import { MetricChart } from "./MetricChart";

// A configured widget slot whose metric has no rows for this account yet — typically a
// metric promoted after the last nightly sync (PAN-423).
const SlotAwaitingData = ({ displayName, title, description }) => (
  <div
    data-testid="usage-slot-awaiting-data"
    className="flex flex-col rounded-lg bg-white p-6 shadow"
  >
    <h3 className="text-lg font-semibold text-gray-800">{displayName}</h3>
    <div className="flex flex-1 flex-col items-center justify-center gap-2 py-8">
      <HiOutlineClock className="h-8 w-8 text-gray-300" />
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <p className="text-xs text-gray-400">{description}</p>
    </div>
  </div>
);

const EmptyState = ({
  icon: Icon,
  title,
  description,
  actionLabel,
  actionTo,
}) => (
  <div className="flex flex-col items-center justify-center gap-4 rounded-lg bg-white p-12 shadow">
    <Icon className="h-12 w-12 text-gray-300" />
    <p className="text-lg text-gray-500">{title}</p>
    <p className="text-sm text-gray-400">{description}</p>
    {actionLabel && actionTo && (
      <Link to={actionTo}>
        <Button color="blue" size="sm">
          <HiCog className="mr-2 h-4 w-4" />
          {actionLabel}
        </Button>
      </Link>
    )}
  </div>
);

export const ProductUsageTab = ({ accountId }) => {
  const { t } = useTranslation();
  const {
    metrics,
    loading: metricsLoading,
    hasData,
    error,
  } = useUsageMetrics(accountId);
  const { status, loading: statusLoading } = useUsageStatus({
    hasData,
    error,
    metricsLoading,
  });
  const { slots, loading: slotsLoading } = useUsageWidgetConfig();
  const productUsageEnabled = useFeatureFlag(
    FEATURE_FLAGS.PRODUCT_USAGE_INTEGRATION,
  );
  // Only the branches that render slots wait for the widget config; the integration /
  // setup / error states never use it.
  const slotsApply = status === "has_data" || status === "no_data";
  const loading = statusLoading || (slotsApply && slotsLoading);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (status === "no_integration") {
    return (
      <EmptyState
        icon={HiOutlineInformationCircle}
        title={t("pages.customer.productUsage.noIntegrationTitle")}
        description={t("pages.customer.productUsage.noIntegrationDescription")}
        actionLabel={t("pages.customer.productUsage.connectNow")}
        actionTo={integrationPath(productUsageEnabled ? "amplitude" : null)}
      />
    );
  }

  if (status === "pending_setup") {
    return (
      <EmptyState
        icon={HiOutlineClock}
        title={t("pages.customer.productUsage.syncingTitle")}
        description={t("pages.customer.productUsage.syncingDescription")}
      />
    );
  }

  if (status === "error") {
    return (
      <EmptyState
        icon={HiOutlineInformationCircle}
        title={t("pages.customer.productUsage.errorTitle")}
        description={t("pages.customer.productUsage.errorDescription")}
      />
    );
  }

  // Configured widget slots win, in slot order, whether or not this account has rows for
  // any of them: a slot whose metric has no rows renders a card naming the metric rather
  // than vanishing. With nothing configured, fall through to the pre-PAN-423 states.
  if (slotsApply && slots.length > 0) {
    const byName = new Map((metrics || []).map((m) => [m.metric_name, m]));
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {slots.map(({ slot, metricName, displayName }) => {
          const metric = byName.get(metricName);
          return metric ? (
            <MetricChart key={`slot-${slot}`} metric={metric} />
          ) : (
            <SlotAwaitingData
              key={`slot-${slot}`}
              displayName={displayName}
              title={t("pages.customer.productUsage.slotNoDataTitle")}
              description={t(
                "pages.customer.productUsage.slotNoDataDescription",
              )}
            />
          );
        })}
      </div>
    );
  }

  if (status === "no_data") {
    return (
      <EmptyState
        icon={HiOutlineClock}
        title={t("pages.customer.productUsage.noDataTitle")}
        description={t("pages.customer.productUsage.noDataDescription")}
      />
    );
  }

  if (status === "has_data") {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {metrics.map((metric) => (
          <MetricChart key={metric.metric_name} metric={metric} />
        ))}
      </div>
    );
  }

  return (
    <EmptyState
      icon={HiOutlineInformationCircle}
      title={t("pages.customer.productUsage.noWidgets")}
      description={t("pages.customer.productUsage.configureDescription")}
      actionLabel={t("pages.customer.productUsage.configure")}
      actionTo={integrationPath(productUsageEnabled ? "amplitude" : null)}
    />
  );
};
